import type { StorageKey } from "./storage.keys";

const listeners = new Set<() => void>();

const localSnapshotCache = new Map<
  string,
  { serialized: string; snapshot: Partial<Record<StorageKey, string | undefined>> }
>();

const sessionSnapshotCache = new Map<
  string,
  { serialized: string; snapshot: Partial<Record<StorageKey, string | undefined>> }
>();

function notify(): void {
  for (const fn of listeners) {
    fn();
  }
}

/**
 * Notify subscribers after storage mutations performed through {@link StorageUtils}
 * or other code that calls this helper.
 */
export function notifyStorageStore(): void {
  notify();
}

/**
 * Subscribe to local/session storage mutations performed through {@link StorageUtils}
 * (and {@link notifyStorageStore}). Used with `useSyncExternalStore`
 * ([docs](https://react.dev/reference/react/useSyncExternalStore)).
 */
export function subscribeStorageStore(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/**
 * Immutable snapshot of `localStorage` keys for `useSyncExternalStore` (cached when serialized value is unchanged).
 */
export function getLocalStorageSnapshot(names: readonly StorageKey[]): Partial<Record<StorageKey, string | undefined>> {
  const sorted = [...names].sort();
  const cacheKey = sorted.join("\0");
  const values: Partial<Record<StorageKey, string | undefined>> = {};
  for (const n of sorted) {
    const raw = localStorage.getItem(n);
    values[n] = raw === null ? undefined : raw;
  }
  const serialized = JSON.stringify(values);
  const hit = localSnapshotCache.get(cacheKey);
  if (hit && hit.serialized === serialized) {
    return hit.snapshot;
  }
  localSnapshotCache.set(cacheKey, { serialized, snapshot: values });
  return values;
}

/**
 * Immutable snapshot of `sessionStorage` keys for `useSyncExternalStore` (cached when serialized value is unchanged).
 */
export function getSessionStorageSnapshot(names: readonly StorageKey[]): Partial<Record<StorageKey, string | undefined>> {
  const sorted = [...names].sort();
  const cacheKey = sorted.join("\0");
  const values: Partial<Record<StorageKey, string | undefined>> = {};
  for (const n of sorted) {
    const raw = sessionStorage.getItem(n);
    values[n] = raw === null ? undefined : raw;
  }
  const serialized = JSON.stringify(values);
  const hit = sessionSnapshotCache.get(cacheKey);
  if (hit && hit.serialized === serialized) {
    return hit.snapshot;
  }
  sessionSnapshotCache.set(cacheKey, { serialized, snapshot: values });
  return values;
}
